import { cloneDeep, get, isNil, set } from 'lodash';
import { all, put, select, takeEvery } from 'redux-saga/effects';

import { FIELD_PATHS, REDUX_STORE } from '@funnels/constants';
import {
  Asset,
  Company,
  Expenses,
  Income,
  Liability,
  PersistApplicationDataCollectionAction,
  ReduxStoreData,
  RelatedParty,
  StoreApplicant,
  StoreEmployment,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import {
  getApplicantId,
  getApplicationId,
  getAssetId,
  waitForResourcesToBeCreated,
  waitForResourcesToBeFree,
} from '../_common';
import {
  createApplicant,
  createApplication,
  persistApplicationDataCollectionFailed,
  persistApplicationDataCollectionSuccess,
  updateApplicant,
  updateApplication,
  updateAsset,
  updateExpenses,
  updateIncome,
  updateLiability,
  updateRelatedParty,
  upsertAssets,
  upsertExpenses,
  upsertIncomes,
  upsertLiabilities,
  upsertRelatedParty,
  upsertV2Employments,
} from '../actions';
import { updateCompany, upsertCompanies } from '../companies/actions';

function* applicationDataCollectionWatcher() {
  yield takeEvery(
    REDUX_STORE.APPLICATION_DATA_COLLECTION.ACTION_TYPES.PERSIST_APPLICATION_DATA_COLLECTION,
    applicationDataCollectionWorker
  );
}

export function* applicationDataCollectionWorker(action: PersistApplicationDataCollectionAction): any {
  try {
    const { formData, isTeamview, customerData, isClone, isHydrated, tracking } = action;
    // The reason why still need to use update rather than upsert because we won't hydra on CV landing page at first
    // and upsert will compare the value to redux store which means even in update case it will still do creation flow in each saga.
    const application = formData.applications && formData.applications[0];
    const isUpdateApplication = !isNil(application.id);

    // Step 1: application create or update
    if (application) {
      if (isUpdateApplication) {
        yield put(updateApplication(application));
      } else {
        if (!isTeamview) {
          set({ applications: [application] }, FIELD_PATHS.APPLICATION_CUSTOMER_ID(0), customerData.id);
        }
        yield put(createApplication(application));
      }
    }

    // wait for application
    const applicationId = yield getApplicationId(application.clientId, application.id);
    // tracking application creation for SNA page
    if (isTeamview && !isClone) {
      tracking.newApplication(
        applicationId,
        get({ applications: [application] }, FIELD_PATHS.APPLICATION_PRIMARY_PURPOSE(0))
      );
    }
    const applicants = get(formData, FIELD_PATHS.APPLICANTS(), []);
    // Step 2: create primary applicant
    if (applicants.length) {
      const firstApplicant = cloneDeep(applicants[0]);
      const isUpdateApplicant = !isNil(firstApplicant.id);

      if (isUpdateApplicant) {
        yield put(updateApplicant(applicationId, firstApplicant, customerData.id));
      } else {
        if (!isTeamview) {
          set(firstApplicant, 'customerId', customerData.id);
          set(firstApplicant, 'contact.mobileNumber', customerData.mobileNumber);
          set(firstApplicant, 'contact.emailAddress', customerData.email);

          // We have had issues with it taking time for the customer first name to become linked to the object
          // so we will default to the applicant firstname, THEN customer firstname
          if (!firstApplicant.firstName) {
            set(firstApplicant, 'firstName', customerData.firstName);
          }
        }
        yield put(createApplicant(applicationId, firstApplicant, customerData.id, isClone));
      }

      // wait for first applicant
      yield getApplicantId(firstApplicant.clientId, firstApplicant.id);
      // Step 3: Create / update the rest of the applicants
      const otherApplicants = applicants.slice(1);

      const otherApplicantsRequest: any[] = [];
      otherApplicants.forEach((applicant: StoreApplicant) => {
        const isUpdateApplicant = !isNil(applicant.id);
        if (isUpdateApplicant) {
          otherApplicantsRequest.push(put(updateApplicant(applicationId, applicant, customerData.id)));
        } else {
          otherApplicantsRequest.push(put(createApplicant(applicationId, applicant, customerData.id, isClone)));
        }
      });
      yield all(otherApplicantsRequest);
    }

    // wait for all applicants
    yield waitForResourcesToBeCreated({
      applicants: applicants.filter((a: StoreApplicant) => a.clientId && !a.id).map((a: StoreApplicant) => a.clientId),
    });

    // Step 4: Create / update first assets
    const assets = get(formData, FIELD_PATHS.ASSETS(), []);
    if (assets.length) {
      if (isHydrated) {
        yield put(upsertAssets(applicationId, assets));
      } else {
        const firstAsset = cloneDeep(assets[0]);
        const isUpdateFirstAsset = !isNil(firstAsset.id);
        if (isUpdateFirstAsset) {
          yield put(updateAsset(applicationId, firstAsset));
        } else {
          yield put(upsertAssets(applicationId, [firstAsset]));
        }

        // wait for first assets
        yield getAssetId(firstAsset.clientId, firstAsset.id);

        // Step 5: Create / update other assets
        const otherAssets = assets.slice(1);
        if (otherAssets.length) {
          const otherAssetsRequest: any[] = [];
          otherAssets.forEach((asset: Asset) => {
            const isUpdateAsset = !isNil(asset.id);
            if (isUpdateAsset) {
              otherAssetsRequest.push(put(updateAsset(applicationId, asset)));
            } else {
              otherAssetsRequest.push(put(upsertAssets(applicationId, [asset])));
            }
          });
          yield all(otherAssetsRequest);
        }
      }
    }
    // wait for all Assets
    yield waitForResourcesToBeCreated({
      assets: assets.filter((a: Asset) => a.clientId && !a.id).map((a: Asset) => a.clientId),
    });
    // Step 6: Create / update liabilities
    const liabilities = get(formData, FIELD_PATHS.LIABILITIES(), []);
    if (liabilities.length) {
      if (isHydrated) {
        yield put(upsertLiabilities(applicationId, liabilities));
      } else {
        const liabilitiesRequest: any[] = [];
        liabilities.forEach((liability: Liability) => {
          const isUpdateLiability = !isNil(liability.id);
          if (isUpdateLiability) {
            liabilitiesRequest.push(put(updateLiability(applicationId, liability)));
          } else {
            liabilitiesRequest.push(put(upsertLiabilities(applicationId, [liability])));
          }
        });
        yield all(liabilitiesRequest);
      }
    }

    // wait for all Liabilities
    yield waitForResourcesToBeCreated({
      liabilities: liabilities.filter((l: Liability) => l.clientId && !l.id).map((l: Liability) => l.clientId),
    });

    // Step 7: Create / update companies
    const companies = get(formData, FIELD_PATHS.COMPANIES(), []);
    if (companies.length) {
      if (isHydrated) {
        yield put(upsertCompanies(applicationId, companies));
      } else {
        const companiesRequest: any[] = [];
        companies.forEach((company: Company) => {
          const isUpdateCompany = !isNil(company.id);
          if (isUpdateCompany) {
            companiesRequest.push(put(updateCompany(applicationId, company)));
          } else {
            companiesRequest.push(put(upsertCompanies(applicationId, [company])));
          }
        });
        yield all(companiesRequest);
      }
    }

    // wait for all companies
    yield waitForResourcesToBeCreated({
      companies: companies.filter((c: Company) => c.clientId && !c.id).map((c: Company) => c.clientId),
    });

    const reduxStoreApplicants: StoreApplicant[] = yield select((state: ReduxStoreData) => state.applicants.data);

    // Step 8: Create / update Employments
    const employments = get(formData, FIELD_PATHS.EMPLOYMENTS(), []);
    if (reduxStoreApplicants.length) {
      const applicantEmploymentsRequest: any[] = [];
      reduxStoreApplicants.forEach((applicant: StoreApplicant) => {
        const applicantEmployments = employments.filter(
          (employment: StoreEmployment) =>
            get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) ===
            get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_CLIENT_ID(0))
        );
        if (applicantEmployments.length) {
          applicantEmploymentsRequest.push(
            put(upsertV2Employments(applicationId, applicantEmployments, applicant.clientId, applicant.id!))
          );
        }
      });
      yield all(applicantEmploymentsRequest);
    }

    // wait for all employments
    yield waitForResourcesToBeCreated({
      employments: employments
        .filter((e: StoreEmployment) => e.clientId && !e.id)
        .map((e: StoreEmployment) => e.clientId),
    });

    // Step 9: Create / update Incomes
    const incomes = get(formData, FIELD_PATHS.INCOMES(), []);
    if (incomes.length) {
      if (isHydrated) {
        yield put(upsertIncomes(applicationId, incomes));
      } else {
        const incomesRequest: any[] = [];
        incomes.forEach((income: Income) => {
          const isUpdateIncome = !isNil(income.id);
          if (isUpdateIncome) {
            incomesRequest.push(put(updateIncome(applicationId, income)));
          } else {
            incomesRequest.push(put(upsertIncomes(applicationId, [income])));
          }
        });
        yield all(incomesRequest);
      }
    }
    // wait for all Incomes
    yield waitForResourcesToBeCreated({
      incomes: incomes.filter((i: Income) => i.clientId && !i.id).map((i: Income) => i.clientId),
    });

    // Step 10: Create / update Expenses
    const expenses = get(formData, FIELD_PATHS.EXPENSES(), []);
    if (expenses.length) {
      if (isHydrated) {
        yield put(upsertExpenses(applicationId, expenses));
      } else {
        const expensesRequest: any[] = [];
        expenses.forEach((expenses: Expenses) => {
          const isUpdateExpenses = !isNil(expenses.id);
          if (isUpdateExpenses) {
            expensesRequest.push(put(updateExpenses(applicationId, expenses)));
          } else {
            expensesRequest.push(put(upsertExpenses(applicationId, [expenses])));
          }
        });
        yield all(expensesRequest);
      }
    }

    // wait for all Expenses
    yield waitForResourcesToBeCreated({
      expenses: expenses.filter((e: Expenses) => e.clientId && !e.id).map((e: Expenses) => e.clientId),
    });

    // Step 11: Update relationships once all applicants have been created.
    yield all(
      reduxStoreApplicants
        .filter((__: any, index: number) =>
          get({ applicants }, FIELD_PATHS.APPLICANT_MARITAL_DETAILS_APPLICANT_ID(index), false)
        )
        .map((applicant: StoreApplicant) => put(updateApplicant(applicationId, applicant, customerData.id)))
    );

    // Step 12: Create / update RelatedParties
    const relatedParties = get(formData, FIELD_PATHS.RELATED_PARTIES(), []);
    if (relatedParties.length) {
      if (isHydrated) {
        yield put(upsertRelatedParty(applicationId!, relatedParties));
      } else {
        const relatedPartiesRequest: any[] = [];
        relatedParties.forEach((relatedParty: RelatedParty) => {
          const isUpdateRelatedParty = !isNil(relatedParty.id);
          if (isUpdateRelatedParty) {
            relatedPartiesRequest.push(put(updateRelatedParty(applicationId, relatedParty)));
          } else {
            relatedPartiesRequest.push(put(upsertRelatedParty(applicationId, [relatedParty])));
          }
        });
        yield all(relatedPartiesRequest);
      }
    }

    // Step 13: If it's update then wait for all responds.
    if (isUpdateApplication) {
      yield waitForResourcesToBeFree([
        'applications',
        'applicants',
        'assets',
        'liabilities',
        'employments',
        'incomes',
        'expenses',
      ]);
    }

    yield put(persistApplicationDataCollectionSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(persistApplicationDataCollectionFailed(e));
  }
}

export default applicationDataCollectionWatcher;
